import React, { useState } from 'react';
import { FlameIcon, PlusIcon, ShieldAlertIcon, XIcon } from 'lucide-react';
import { AppShell } from '../../../components/layout/AppShell';
import { SubNav, accountNav } from '../../../components/layout/SubNav';
import { Card } from '../../../components/ui/Panels';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { Field, Input, Select, Textarea } from '../../../components/ui/Field';
import { customer, severityChip, severityChipIdle, severityLabel, severityTone } from '../../../data/customer';
import { allergens, diets } from '../../../data/taxonomy';
import type { Allergy, AllergySeverity } from '../../../types/customer';
const severities: AllergySeverity[] = ['avoid', 'strict', 'anaphylaxis'];
const severityHelp: Record<AllergySeverity, string> = {
  avoid: 'Leave it out of my dishes.',
  strict: 'No cross-contact — separate boards, pans and oil.',
  anaphylaxis: 'Medical emergency risk. Nothing from this ingredient enters the kitchen.'
};
export function Dietary() {
  const [allergies, setAllergies] = useState<Allergy[]>(customer.dietary.allergies);
  const [selectedDiets, setSelectedDiets] = useState<string[]>(customer.dietary.diets);
  const [dislikes, setDislikes] = useState(customer.dietary.dislikes.join(', '));
  const [spice, setSpice] = useState(customer.dietary.spiceTolerance);
  const [notes, setNotes] = useState(customer.dietary.notes);
  const [newAllergen, setNewAllergen] = useState(allergens[0]);
  const [newSeverity, setNewSeverity] = useState<AllergySeverity>('strict');
  const [newMember, setNewMember] = useState(customer.dietary.members[0].id);
  const add = () => {
    if (allergies.some(a => a.allergen === newAllergen && a.memberId === newMember)) return;
    setAllergies([...allergies, {
      id: `al-${Date.now()}`,
      allergen: newAllergen,
      severity: newSeverity,
      memberId: newMember
    }]);
  };
  const update = (id: string, severity: AllergySeverity) => setAllergies(allergies.map(a => a.id === id ? {
    ...a,
    severity
  } : a));
  return <AppShell title="Dietary profile" subtitle="Carried into every request, chat and quote automatically" data-id="element-1485" data-mp="n8vs2hwom2fo">
      <SubNav items={accountNav} data-id="element-1486" data-mp="99w4ldkwntl3" />

      <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px]" data-id="element-1487" data-mp="9ap5hxx6novb">
        <div className="space-y-6" data-id="element-1488" data-mp="e8qmmdb1te45">
          <Card className="p-5" data-id="element-1489" data-mp="e9s8lctlrl0n">
            <div className="flex items-center gap-2" data-id="element-1490" data-mp="11x04j4555hq0">
              <ShieldAlertIcon className="h-4 w-4 text-danger" aria-hidden="true" data-id="element-1491" data-mp="11zmj3nqg8xhq" />
              <h2 className="font-display text-lg font-semibold text-ink" data-id="element-1492" data-mp="15ylikstew21s">Allergies</h2>
            </div>
            <p className="mt-1 text-sm text-ink-2" data-id="element-1493" data-mp="s7gixqq1n9rg">
              Severity matters more than the list. A chef has to acknowledge this before they can send you a quote.
            </p>

            <ul className="mt-4 space-y-2.5" data-id="element-1494" data-mp="1kut5i0w7fhkr">
              {allergies.map(allergy => {
              const member = customer.dietary.members.find(m => m.id === allergy.memberId);
              return <li key={allergy.id} className="rounded-lg border border-hairline p-3.5" data-id="element-1495" data-mp="1z034lac6lpb4">
                    <div className="flex flex-wrap items-center gap-3" data-id="element-1496" data-mp="1v02ir4mr6bu1">
                      <span className="font-medium text-ink" data-id="element-1497" data-mp="28qo3yzzoixv3">{allergy.allergen}</span>
                      {member && <span className="text-[13px] text-ink-3" data-id="element-1498" data-mp="23tnyaq6qrx4i">{member.name}</span>}
                      <Badge tone={severityTone[allergy.severity]} className="ml-auto" data-id="element-1499" data-mp="1g2ikegkd3ewu">
                        {severityLabel[allergy.severity]}
                      </Badge>
                      <button type="button" onClick={() => setAllergies(allergies.filter(a => a.id !== allergy.id))} className="rounded p-1 text-ink-3 hover:text-danger" aria-label={`Remove ${allergy.allergen}`} data-id="element-1500" data-mp="1q182a9c9czd3">
                        <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-1501" data-mp="2wg8o3m7kk15y" />
                      </button>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-1.5" data-id="element-1502" data-mp="16210pcf3j7dr">
                      {severities.map(level => <button key={level} type="button" onClick={() => update(allergy.id, level)} aria-pressed={allergy.severity === level} className={`rounded-full border px-3 py-1 text-[12px] font-medium transition-colors duration-150 ease-out ${allergy.severity === level ? severityChip[level] : severityChipIdle[level]}`} data-id="element-1503" data-mp="1b147p2emznod">
                          {severityLabel[level]}
                        </button>)}
                    </div>
                    <p className="mt-2 text-[12px] text-ink-3" data-id="element-1504" data-mp="27r4s6p7z3hzv">{severityHelp[allergy.severity]}</p>
                    {allergy.note && <p className="mt-1.5 text-[13px] text-ink-2" data-id="element-1505" data-mp="2cmk43bmf73tn">{allergy.note}</p>}
                  </li>;
            })}
            </ul>

            <div className="mt-4 flex flex-wrap items-end gap-3 border-t border-hairline pt-4" data-id="element-1506" data-mp="3s7b6nxnqw508">
              <Field label="Allergen" id="d-allergen" className="min-w-[10rem] flex-1" data-id="element-1507" data-mp="3o57lghb9xm1r">
                <Select id="d-allergen" value={newAllergen} onChange={e => setNewAllergen(e.target.value)} data-id="element-1508" data-mp="3j7frlhr2jpux">
                  {allergens.map(a => <option key={a} value={a} data-id="element-1509" data-mp="jtvl7ro3g5qq">
                      {a}
                    </option>)}
                </Select>
              </Field>
              <Field label="Who" id="d-member" className="min-w-[9rem]" data-id="element-1510" data-mp="ayfn2ygb34fc">
                <Select id="d-member" value={newMember} onChange={e => setNewMember(e.target.value)} data-id="element-1511" data-mp="60w8tyjlyrne">
                  {customer.dietary.members.map(m => <option key={m.id} value={m.id} data-id="element-1512" data-mp="13huu0wz3l2ti">
                      {m.name} · {m.relation}
                    </option>)}
                </Select>
              </Field>
              <Field label="Severity" id="d-sev" className="min-w-[9rem]" data-id="element-1513" data-mp="1hnddejdj1i53">
                <Select id="d-sev" value={newSeverity} onChange={e => setNewSeverity(e.target.value as AllergySeverity)} data-id="element-1514" data-mp="1mjvf1adk90d7">
                  {severities.map(s => <option key={s} value={s} data-id="element-1515" data-mp="1ctscojkjua27">
                      {severityLabel[s]}
                    </option>)}
                </Select>
              </Field>
              <Button type="button" variant="primary" onClick={add} data-id="element-1516" data-mp="13tm49c3pk09w">
                <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-1517" data-mp="13rsv9q5mh3yq" />
                Add
              </Button>
            </div>
          </Card>

          <Card className="p-5" data-id="element-1518" data-mp="tv6d594okr0e">
            <h2 className="font-display text-lg font-semibold text-ink" data-id="element-1519" data-mp="tw7x633ocnu9">Diets and preferences</h2>
            <p className="mt-1 text-sm text-ink-2" data-id="element-1520" data-mp="2k8ncwkw3k0bp">Filters chefs by capability and shapes what they propose.</p>
            <div className="mt-4 flex flex-wrap gap-1.5" data-id="element-1521" data-mp="2o8xqojq4rv14">
              {diets.map(diet => {
              const on = selectedDiets.includes(diet);
              return <button key={diet} type="button" aria-pressed={on} onClick={() => setSelectedDiets(on ? selectedDiets.filter(d => d !== diet) : [...selectedDiets, diet])} className={`rounded-full border px-3 py-1.5 text-[13px] font-medium transition-colors duration-150 ease-out ${on ? 'border-primary bg-primary-soft text-primary' : 'border-hairline text-ink-2 hover:text-ink'}`} data-id="element-1522" data-mp="8m3ewxko681m">
                    {diet}
                  </button>;
            })}
            </div>

            <Field label="Dislikes" id="d-dislikes" hint="not allergies — just things you would rather not see" className="mt-5" data-id="element-1523" data-mp="3737afart5iy7">
              <Input id="d-dislikes" value={dislikes} onChange={e => setDislikes(e.target.value)} data-id="element-1524" data-mp="325ffs8zs3iqb" />
            </Field>

            <div className="mt-5" data-id="element-1525" data-mp="2l9094f5kaa94">
              <label htmlFor="d-spice" className="flex items-center justify-between gap-3 text-[13px] font-semibold text-ink" data-id="element-1526" data-mp="2l871zd2oxiqg">
                <span className="flex items-center gap-1.5" data-id="element-1527" data-mp="2q0qlsrrz1a3p">
                  <FlameIcon className="h-4 w-4 text-action" aria-hidden="true" data-id="element-1528" data-mp="2uy0a243mjfpy" />
                  Chilli tolerance
                </span>
                <span className="tnum text-ink-2" data-id="element-1529" data-mp="1yavnw6q1sc3f">{spice} / 5</span>
              </label>
              <input id="d-spice" type="range" min={1} max={5} value={spice} onChange={e => setSpice(Number(e.target.value))} className="mt-2 w-full accent-action" data-id="element-1530" data-mp="22anzytenh0dj" />
              <p className="mt-1 text-[12px] text-ink-3" data-id="element-1531" data-mp="3io4bc4tcb3ds">
                Thai chefs ask this first. 1 is no heat at all, 5 is as hot as the dish is meant to be.
              </p>
            </div>

            <Field label="Anything else a chef should know" id="d-notes" className="mt-5" data-id="element-1532" data-mp="1bzub8zv0upt0">
              <Textarea id="d-notes" rows={3} value={notes} onChange={e => setNotes(e.target.value)} data-id="element-1533" data-mp="172lt2z9vilrr" />
            </Field>

            <Button type="button" variant="action" className="mt-5" data-id="element-1534" data-mp="128hg9zdxfjir">
              Save dietary profile
            </Button>
          </Card>
        </div>

        <aside className="space-y-4" data-id="element-1535" data-mp="taxf0qg2dn85">
          <Card className="p-5" data-id="element-1536" data-mp="t7t316wcxn6x">
            <h2 className="text-[12px] font-semibold uppercase tracking-[0.05em] text-ink-3" data-id="element-1537" data-mp="ob2m6hcqankg">Household</h2>
            <ul className="mt-3 space-y-2" data-id="element-1538" data-mp="jb6i6i416w4w">
              {customer.dietary.members.map(member => <li key={member.id} className="flex items-baseline justify-between gap-3" data-id="element-1539" data-mp="ed54kcpdjp0t">
                  <span className="text-sm font-medium text-ink" data-id="element-1540" data-mp="2dq964bdfm65p">{member.name}</span>
                  <span className="text-[12px] text-ink-3" data-id="element-1541" data-mp="28qmozquhg5rm">{member.relation}</span>
                </li>)}
            </ul>
            <Button type="button" variant="ghost" size="sm" className="mt-3" data-id="element-1542" data-mp="24s9vvicqiezr">
              <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-1543" data-mp="1zuq6bmal6fsi" />
              Add someone
            </Button>
          </Card>

          <Card className="border-danger/30 bg-danger-soft p-5" data-id="element-1544" data-mp="2eq64o3i78xup">
            <h2 className="text-[12px] font-semibold uppercase tracking-[0.05em] text-danger" data-id="element-1545" data-mp="29rutnpg64haw">Why severity matters</h2>
            <p className="mt-2 text-[13px] leading-relaxed text-danger" data-id="element-1546" data-mp="1m3v4gtyi8yyf">
              “Avoid” lets a chef leave an ingredient out. “Strict” and “anaphylaxis” change how they shop, which pans they
              bring, and whether they will take the booking at all. Please be precise.
            </p>
          </Card>
        </aside>
      </div>
    </AppShell>;
}